import type { WarehouseInvoiceSummary } from '@/services/warehouseTypes'

export type ProductInvoiceLinkStatus = 'resolved' | 'partial' | 'not_linked' | 'no_lines'

export type ProductInvoiceLinkBadge = {
  label: string
  className: string
}

const LINKED_CLASS = 'bg-emerald-50 text-emerald-800 ring-emerald-200'
const IGNORED_CLASS = 'bg-gray-100 text-gray-700 ring-gray-200'
const AMBER_CLASS = 'bg-amber-50 text-amber-800 ring-amber-200'

function productLines(invoice: WarehouseInvoiceSummary) {
  return invoice.lines.filter((line) => !line.is_service_fee)
}

export function productLineHasLinkedTxn(
  line: WarehouseInvoiceSummary['lines'][number]
): boolean {
  if (line.linked_bk_txn_ids?.length) return true
  return Boolean(line.linked_bk_txn_id)
}

function isProductLineResolved(line: WarehouseInvoiceSummary['lines'][number]): boolean {
  return Boolean(line.ignored || productLineHasLinkedTxn(line))
}

export function getProductInvoiceLinkStatus(
  invoice: WarehouseInvoiceSummary
): ProductInvoiceLinkStatus {
  const lines = productLines(invoice)
  if (lines.length === 0) return 'no_lines'

  const resolvedCount = lines.filter(isProductLineResolved).length
  if (resolvedCount === lines.length) return 'resolved'
  if (resolvedCount === 0) return 'not_linked'
  return 'partial'
}

export function productInvoiceLinkBadges(
  invoice: WarehouseInvoiceSummary
): ProductInvoiceLinkBadge[] {
  const lines = productLines(invoice)
  if (lines.length === 0) return []

  const linkedCount = lines.filter((line) => productLineHasLinkedTxn(line) && !line.ignored).length
  const ignoredCount = lines.filter((line) => line.ignored).length
  const status = getProductInvoiceLinkStatus(invoice)

  if (status === 'resolved') {
    if (linkedCount === lines.length) {
      return [{ label: 'Linked', className: LINKED_CLASS }]
    }
    if (ignoredCount === lines.length) {
      return [{ label: 'Ignored', className: IGNORED_CLASS }]
    }
    if (linkedCount > 0 && ignoredCount > 0) {
      return [
        { label: `Linked (${linkedCount})`, className: LINKED_CLASS },
        { label: `Ignored (${ignoredCount})`, className: IGNORED_CLASS },
      ]
    }
    return []
  }

  if (status === 'not_linked') {
    return [{ label: 'Not linked', className: AMBER_CLASS }]
  }
  if (status === 'partial') {
    return [{ label: 'Partial', className: AMBER_CLASS }]
  }
  return []
}
